/**
 * THE ON-DEMAND VENDOR SCRIPTS (launch-marketing-pages design M2, "The
 * head"; spec `public-marketing-pages` § "The layout head loads only what a
 * surface uses").
 *
 * The layout head loads no vendor SDK a surface may not use. Stripe.js and
 * LogRocket load here instead, when the surface that uses them asks:
 *   - `loadVendorScript(name)` appends the script once per name and resolves
 *     with its global. It resolves at once when the global already exists
 *     (a test's stub, or a load that already ran), and with `null` when the
 *     vendor is disabled on this page.
 *   - `vendorScriptLoaded(name)` only waits: it resolves when some other
 *     caller's load does, and never starts one itself.
 *
 * The roster is the one place JS names a vendor URL. Each host stays on the
 * `platform-runtime-baseline` carve-out list and in the CSP host inventory
 * (config/initializers/content_security_policy.rb).
 *
 * A leaf: it imports nothing, so the boot graph never pulls a workspace
 * module through it.
 */

const ROSTER = {
  stripe: {
    src: "https://js.stripe.com/v3/",
    global: "Stripe",
  },
  logrocket: {
    src: "https://cdn.logrocket.io/LogRocket.min.js",
    global: "LogRocket",
    crossOrigin: "anonymous",
    // The production head declares the app id (`window.thLogRocketAppId`),
    // the way it declares Segment's `analytics`; no other page records.
    enabled: () => Boolean(globalThis.thLogRocketAppId),
    onload: (LogRocket) => LogRocket.init(globalThis.thLogRocketAppId),
  },
};

const loads = new Map();
const waiters = new Map();

function vendor(name) {
  const entry = ROSTER[name];
  if (!entry) throw new Error(`Unknown vendor script: ${name}`);
  return entry;
}

function isEnabled(entry) {
  return entry.enabled ? entry.enabled() : true;
}

function waiter(name) {
  if (!waiters.has(name)) {
    let resolve;
    const promise = new Promise((done) => {
      resolve = done;
    });
    waiters.set(name, { promise, resolve });
  }
  return waiters.get(name);
}

function appendScript(name, entry) {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = entry.src;
    script.async = true;
    if (entry.crossOrigin) script.crossOrigin = entry.crossOrigin;
    script.onload = () => {
      const loaded = globalThis[entry.global] ?? null;
      if (loaded && entry.onload) entry.onload(loaded);
      waiter(name).resolve(loaded);
      resolve(loaded);
    };
    // A failed load is not memoized: the next caller tries again, and a
    // waiter keeps waiting for that try.
    script.onerror = () => {
      loads.delete(name);
      script.remove();
      reject(new Error(`Could not load ${name}`));
    };
    document.head.appendChild(script);
  });
}

export function loadVendorScript(name) {
  const entry = vendor(name);
  const existing = globalThis[entry.global];
  if (existing) return Promise.resolve(existing);
  if (!isEnabled(entry)) return Promise.resolve(null);
  if (!loads.has(name)) loads.set(name, appendScript(name, entry));
  return loads.get(name);
}

export function vendorScriptLoaded(name) {
  const entry = vendor(name);
  const existing = globalThis[entry.global];
  if (existing) return Promise.resolve(existing);
  if (!isEnabled(entry)) return Promise.resolve(null);
  return waiter(name).promise;
}
